import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect, useCallback } from "react";
import { subirDatos, eliminarDato } from "./syncSupabase";
import { authHeader } from './supabase';

const STORAGE_KEY = 'alqos_poes';
const storage = {
  get: (def=[]) => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||JSON.stringify(def)); } catch { return def; } },
  set: (v) => { try { localStorage.setItem(STORAGE_KEY,JSON.stringify(v)); } catch {} },
};

const Q = {
  azul:'#1C3FAA', azulVivo:'#2A7FFF', grisClaro:'#E8E9F6',
  grisPalido:'#F4F6FB', grisOscuro:'#2B2F36', grisPlata:'#8E93A1',
};

const MODULOS_ALQOS = [
  { id:'preprensa',  label:'Pre Prensa',          icon:'🖨️' },
  { id:'ots',        label:'Órdenes de Trabajo',  icon:'📋' },
  { id:'ingresos',   label:'Ingreso de Materiales',icon:'📦' },
  { id:'nc',         label:'No Conformidades',    icon:'⚠️' },
  { id:'auditoria',  label:'Auditorías',           icon:'🔍' },
  { id:'haccp',      label:'HACCP',               icon:'🧫' },
  { id:'capacitaciones', label:'Capacitaciones',  icon:'🎓' },
  { id:'general',    label:'General / Transversal',icon:'🏭' },
];

const CATEGORIAS = [
  'Proceso productivo','Calidad','Higiene y limpieza','Seguridad','Ambiental',
  'Logística','Mantenimiento','Administración','HACCP','FSC','Otro',
];

const ESTADOS_POE = {
  borrador:   { label:'📝 Borrador',   color:'#f59e0b' },
  revision:   { label:'🔄 En revisión',color:'#3b82f6' },
  vigente:    { label:'✅ Vigente',     color:'#22c55e' },
  obsoleto:   { label:'📁 Obsoleto',   color:'#808085' },
};

function generarCodigo(lista) { return `POE-${new Date().getFullYear()}-${String(lista.length+1).padStart(3,'0')}`; }
function nuevaVersion(ver) {
  const [mayor, menor] = (ver||'1.0').split('.').map(Number);
  return `${mayor}.${(menor||0)+1}`;
}

// ─── ARIA API ─────────────────────────────────────────────────
async function llamarARIA(prompt) {
  const response = await fetch("/api/aria", {
    method: "POST",
    headers: { ...(await authHeader()),
      "Content-Type": "application/json",
      "anthropic-version": "2023-06-01",
      },
    body: JSON.stringify({ model:"claude-sonnet-4-5", max_tokens:2000, messages:[{role:"user",content:prompt}] }),
  });
  const data = await response.json();
  return (data.content?.[0]?.text||"").replace(/```json/g,"").replace(/```/g,"").trim();
}

export default function POEs({ onVolver, oscuro, usuario }) {
  const [poes, setPoes]               = useState(() => storage.get([]));
  const [vista, setVista]             = useState('listado');
  const [poeActual, setPoeActual]     = useState(null);
  const [seccionActiva, setSeccionActiva] = useState('info');
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroModulo] = useState('todos');
  const [busqueda, setBusqueda]         = useState('');

  // ARIA estados
  const [cargandoARIA, setCargandoARIA] = useState(false);
  const [panelARIA, setPanelARIA]       = useState(null); // 'pasos'|'revision'|'simplificar'|'vencidos'
  const [resultadoARIA, setResultadoARIA] = useState(null);
  const [descripcionARIA, setDescripcionARIA] = useState('');
  const [alertasVencidos, setAlertasVencidos] = useState([]);

  const [form, setForm] = useState({
    titulo:'', codigo:'', categoria:CATEGORIAS[0], modulos:[], objetivo:'',
    alcance:'', responsable:'', frecuenciaRevision:'Anual',
    pasos:[], materiales:[], registrosAsociados:[],
  });

  const [formPaso, setFormPaso] = useState({ descripcion:'', responsable:'', criterioAceptacion:'', advertencia:'' });
  const [editandoPaso, setEditandoPaso] = useState(null);
  const [formLectura, setFormLectura] = useState({ operador:'', legajo:'', observaciones:'' });
  const [formMaterial, setFormMaterial] = useState('');

  const esAdmin = usuario?.rol === 'Administrador';

  const bg     = oscuro ? '#09184D' : Q.grisPalido;
  const cardBg = oscuro ? '#0d2160' : '#FFFFFF';
  const texto  = oscuro ? '#EDF2FA' : Q.grisOscuro;
  const borde  = oscuro ? '#1e3a8a' : Q.grisClaro;
  const navBg  = oscuro ? '#060f30' : Q.azul;
  const sub    = oscuro ? '#9aabd4' : Q.grisPlata;
  const card   = { background:cardBg, borderRadius:'14px', padding:'1.5rem', border:`1px solid ${borde}`, marginBottom:'12px' };
  const inp    = { width:'100%', padding:'9px 12px', borderRadius:'10px', border:`1.5px solid ${borde}`, fontSize:'13px', boxSizing:'border-box', marginTop:'4px', background:cardBg, color:texto, fontFamily:'sans-serif' };
  const lbl    = { fontSize:'11px', color:sub, fontWeight:'700', display:'block', marginTop:'12px', textTransform:'uppercase', letterSpacing:'.05em' };
  const btn    = (col=Q.azulVivo) => ({ padding:'8px 18px', background:col, color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'13px', fontWeight:'700' });

  // ─── SUPABASE ─────────────────────────────────────────────────
  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('poes').select('data');
        if (data?.length > 0) {
          const todos = data.map(r=>r.data).filter(Boolean);
          if (todos.length > 0) { setPoes(todos); storage.set(todos); }
        }
      } catch(e) { console.warn('POEs Supabase:', e); }
    }
    cargar();
  }, []);

  // ─── VERIFICAR VENCIDOS AL MONTAR ─────────────────────────────
  useEffect(() => {
    const hoy = new Date();
    const vencidos = poes.filter(p => {
      if (p.estado === 'obsoleto') return false;
      if (!p.ultimaModificacion) return false;
      const ultima = new Date(p.ultimaModificacion);
      const meses = { Mensual:1, Trimestral:3, Semestral:6, Anual:12, 'Cuando haya cambios':999 };
      const intervalo = meses[p.frecuenciaRevision] || 12;
      const proxima = new Date(ultima);
      proxima.setMonth(proxima.getMonth() + intervalo);
      return hoy > proxima;
    });
    setAlertasVencidos(vencidos);
  }, [poes]);

  const guardarPoes = useCallback((nuevos) => {
    setPoes(nuevos);
    storage.set(nuevos);
    subirDatos(STORAGE_KEY);
  }, []);

  const actualizarPoe = useCallback((cambios) => {
    setPoeActual(prev => {
      const updated = { ...prev, ...cambios, ultimaModificacion:new Date().toISOString(), modificadoPor:usuario?.nombre };
      setPoes(ps => {
        const nuevos = ps.map(p => p.id===updated.id ? updated : p);
        storage.set(nuevos);
        subirDatos(STORAGE_KEY);
        return nuevos;
      });
      return updated;
    });
  }, [usuario]);

  // ════════════════════════════════════════════════════════════
  // ARIA — FUNCIONES
  // ════════════════════════════════════════════════════════════

  // 1. Generar pasos desde descripción
  const ariaGenerarPasos = async () => {
    if (!descripcionARIA.trim()) { alert('Describí el procedimiento primero.'); return; }
    setCargandoARIA(true);
    setResultadoARIA(null);
    try {
      const contexto = poeActual
        ? `Título: ${poeActual.titulo}\nCategoría: ${poeActual.categoria}\nMódulos vinculados: ${(poeActual.modulos||[]).join(', ')}\nObjetivo: ${poeActual.objetivo||'No especificado'}`
        : `Título: ${form.titulo}\nCategoría: ${form.categoria}`;

      const prompt = `Sos ARIA, experto en sistemas de gestión de calidad ISO 9001 y procedimientos operativos. Generá los pasos detallados para este procedimiento en una pyme industrial argentina de packaging.

${contexto}
Descripción del procedimiento: ${descripcionARIA}

Generá máximo 10 pasos claros, concisos y accionables. Cada paso debe poder ejecutarlo un operario.
JSON exacto sin texto antes ni después:
[{"descripcion":"Qué hacer exactamente","responsable":"cargo o rol","criterioAceptacion":"cómo sé que está bien hecho","advertencia":"punto crítico o riesgo si aplica, sino dejar vacío"}]`;

      const raw = await llamarARIA(prompt);
      setResultadoARIA(JSON.parse(raw));
    } catch(e) {
      setResultadoARIA({ error: 'Error al generar pasos. Verificá la API key.' });
    }
    setCargandoARIA(false);
  };

  const aplicarPasosARIA = () => {
    if (!Array.isArray(resultadoARIA)) return;
    const pasos = resultadoARIA.map((p,i) => ({ ...p, id:Date.now()+i, orden:i+1, origenARIA:true }));
    if (poeActual) {
      actualizarPoe({ pasos: [...(poeActual.pasos||[]), ...pasos] });
    } else {
      setForm(f => ({ ...f, pasos: [...(f.pasos||[]), ...pasos] }));
    }
    setPanelARIA(null);
    setResultadoARIA(null);
    setDescripcionARIA('');
    if (poeActual) setSeccionActiva('pasos');
  };

  // 2. Revisión de consistencia del POE
  const ariaRevisarPOE = async () => {
    if (!poeActual || !(poeActual.pasos||[]).length) { alert('El POE necesita tener pasos cargados para revisarlos.'); return; }
    setCargandoARIA(true);
    setPanelARIA('revision');
    setResultadoARIA(null);
    try {
      const prompt = `Sos ARIA, experto en sistemas de calidad ISO 9001. Revisá este POE y detectá inconsistencias, pasos ambiguos o mejoras necesarias.

POE: ${poeActual.titulo}
Categoría: ${poeActual.categoria}
Objetivo: ${poeActual.objetivo||'No especificado'}
Alcance: ${poeActual.alcance||'No especificado'}

Pasos actuales:
${(poeActual.pasos||[]).map((p,i)=>`${i+1}. ${p.descripcion}${p.criterioAceptacion?' [Criterio: '+p.criterioAceptacion+']':''}`).join('\n')}

Analizá y respondé JSON sin texto antes ni después:
{"puntuacion":85,"problemas":[{"paso":2,"tipo":"ambiguo","descripcion":"El paso 2 no especifica...","sugerencia":"Reemplazarlo por..."}],"faltantes":["Criterio de aceptación en paso 3","Advertencia en paso 5"],"fortalezas":["Clara definición de responsable","Criterios medibles"],"recomendacionGeneral":"Texto de recomendación general"}`;

      const raw = await llamarARIA(prompt);
      setResultadoARIA(JSON.parse(raw));
    } catch(e) {
      setResultadoARIA({ error: 'Error al revisar el POE.' });
    }
    setCargandoARIA(false);
  };

  // 3. Simplificar lenguaje para operarios
  const ariaSimplificar = async () => {
    if (!poeActual || !(poeActual.pasos||[]).length) { alert('Necesitás tener pasos cargados.'); return; }
    setCargandoARIA(true);
    setPanelARIA('simplificar');
    setResultadoARIA(null);
    try {
      const prompt = `Sos ARIA, experto en comunicación industrial. Reescribí los pasos de este procedimiento en lenguaje simple y directo para operarios sin formación técnica. Mantené el mismo significado pero con palabras de uso cotidiano, oraciones cortas y verbos de acción directos.

POE: ${poeActual.titulo}
Pasos originales:
${(poeActual.pasos||[]).map((p,i)=>`${i+1}. ${p.descripcion}`).join('\n')}

JSON exacto sin texto antes ni después (misma cantidad de pasos):
[{"original":"texto original","simplificado":"texto simplificado en lenguaje operario"}]`;

      const raw = await llamarARIA(prompt);
      setResultadoARIA(JSON.parse(raw));
    } catch(e) {
      setResultadoARIA({ error: 'Error al simplificar.' });
    }
    setCargandoARIA(false);
  };

  const aplicarSimplificacion = () => {
    if (!Array.isArray(resultadoARIA)) return;
    const pasosSimplificados = (poeActual.pasos||[]).map((p,i) => ({
      ...p,
      descripcionOriginal: p.descripcion,
      descripcion: resultadoARIA[i]?.simplificado || p.descripcion,
      simplificadoPorARIA: true,
    }));
    actualizarPoe({ pasos: pasosSimplificados });
    setPanelARIA(null);
    setResultadoARIA(null);
  };

  // 4. Detectar POEs vencidos + alerta
  const ariaAlertaVencidos = async () => {
    if (alertasVencidos.length === 0) { alert('¡Todos los POEs están al día! No hay vencidos.'); return; }
    setCargandoARIA(true);
    setPanelARIA('vencidos');
    setResultadoARIA(null);
    try {
      const prompt = `Sos ARIA, experto en gestión de calidad. Analizá estos POEs vencidos y generá un resumen ejecutivo con prioridades de revisión.

POEs con revisión vencida:
${alertasVencidos.map(p=>`- ${p.codigo}: ${p.titulo} (${p.categoria}) — Última mod: ${p.ultimaModificacion?.slice(0,10)} — Frecuencia: ${p.frecuenciaRevision} — Estado: ${p.estado}`).join('\n')}

Generá JSON con prioridades sin texto antes ni después:
{"resumen":"texto del resumen ejecutivo","prioridades":[{"codigo":"POE-2026-001","titulo":"título","razon":"por qué es prioritario","accionRecomendada":"qué hacer","urgencia":"alta/media/baja"}],"recomendacionGeneral":"texto"}`;

      const raw = await llamarARIA(prompt);
      setResultadoARIA(JSON.parse(raw));
    } catch(e) {
      setResultadoARIA({ error: 'Error al analizar vencidos.' });
    }
    setCargandoARIA(false);
  };

  // 5. Vinculación inteligente con NC
  const ariaVinculacionNC = async () => {
    try {
      const ncs = JSON.parse(localStorage.getItem('alqos_ncs')||'[]');
      const ncsAbiertas = ncs.filter(n=>n.estado==='abierta').slice(0,5);
      if (ncsAbiertas.length === 0) { alert('No hay NCs abiertas para analizar.'); return; }
      setCargandoARIA(true);
      setPanelARIA('nc_vinculacion');
      setResultadoARIA(null);

      const prompt = `Sos ARIA, experto en sistemas de calidad. Analizá estas NCs abiertas y determiná qué POEs deberían revisarse o actualizarse.

NCs abiertas recientes:
${ncsAbiertas.map(n=>`- ${n.codigo}: ${n.titulo} (${n.tipo||'proceso'}) — ${n.descripcion?.slice(0,100)}`).join('\n')}

POEs disponibles:
${poes.filter(p=>p.estado!=='obsoleto').map(p=>`- ${p.codigo}: ${p.titulo} (${p.categoria}) — Módulos: ${(p.modulos||[]).join(', ')}`).join('\n')||'Sin POEs cargados'}

JSON exacto sin texto antes ni después:
[{"nc":"NC-código","poe":"POE-código","relacion":"por qué están relacionados","accion":"revisar/actualizar/crear nuevo"}]`;

      const raw = await llamarARIA(prompt);
      setResultadoARIA(JSON.parse(raw));
    } catch(e) {
      setResultadoARIA({ error: 'Error al analizar vinculación.' });
    }
    setCargandoARIA(false);
  };

  // ─── HELPERS ─────────────────────────────────────────────────
  const crearPoe = () => {
    if (!form.titulo?.trim()) { alert('⚠️ El título es obligatorio.'); return; }
    const nuevo = { ...form, id:Date.now(), codigo:form.codigo?.trim()||generarCodigo(poes), version:'1.0', estado:'borrador', fechaCreacion:new Date().toISOString(), creadoPor:usuario?.nombre||'Sistema', ultimaModificacion:new Date().toISOString(), historialVersiones:[], lecturas:[] };
    const nuevos = [...poes, nuevo];
    guardarPoes(nuevos);
    setPoeActual(nuevo);
    setSeccionActiva('pasos');
    setVista('detalle');
  };

  const eliminarPoe = async (id) => { guardarPoes(poes.filter(p=>p.id!==id)); await eliminarDato(STORAGE_KEY, id); setConfirmEliminar(null); setVista('listado'); setPoeActual(null); };

  const nuevaVersionPoe = () => {
    if (!window.confirm(`¿Crear nueva versión v${nuevaVersion(poeActual.version)}?`)) return;
    const hist = [...(poeActual.historialVersiones||[]), { version:poeActual.version, fecha:new Date().toISOString(), usuario:usuario?.nombre, estado:poeActual.estado, pasos:poeActual.pasos }];
    actualizarPoe({ version:nuevaVersion(poeActual.version), estado:'borrador', historialVersiones:hist, lecturas:[] });
  };

  const registrarLectura = () => {
    if (!formLectura.operador?.trim()) { alert('⚠️ Ingresá tu nombre.'); return; }
    const lectura = { id:Date.now(), ...formLectura, fecha:new Date().toISOString(), version:poeActual.version };
    actualizarPoe({ lecturas:[...(poeActual.lecturas||[]), lectura] });
    setFormLectura({ operador:'', legajo:'', observaciones:'' });
    alert(`✅ Lectura registrada. ${lectura.operador} confirmó ${poeActual.codigo} v${poeActual.version}.`);
    setVista('detalle');
  };

  const agregarPaso = () => {
    if (!formPaso.descripcion.trim()) return;
    const pasos = poeActual.pasos||[];
    actualizarPoe({ pasos:[...pasos,{...formPaso,id:Date.now(),orden:pasos.length+1}] });
    setFormPaso({ descripcion:'', responsable:'', criterioAceptacion:'', advertencia:'' });
  };

  const moverPaso = (id, dir) => {
    const pasos = [...(poeActual.pasos||[])];
    const idx = pasos.findIndex(p=>p.id===id);
    if ((dir==='up'&&idx===0)||(dir==='down'&&idx===pasos.length-1)) return;
    const swap = dir==='up'?idx-1:idx+1;
    [pasos[idx],pasos[swap]] = [pasos[swap],pasos[idx]];
    pasos.forEach((p,i)=>p.orden=i+1);
    actualizarPoe({ pasos });
  };

  const poesFiltrados = poes.filter(p => {
    const matchEstado  = filtroEstado==='todos'||p.estado===filtroEstado;
    const matchModulo  = filtroModulo==='todos'||(p.modulos||[]).includes(filtroModulo);
    const matchBusqueda = !busqueda||[p.titulo,p.codigo,p.categoria].some(v=>v?.toLowerCase().includes(busqueda.toLowerCase()));
    return matchEstado&&matchModulo&&matchBusqueda;
  });

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{background:navBg,padding:'1rem 1.5rem',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100,boxShadow:'0 2px 12px rgba(0,0,0,.3)',marginBottom:'1.5rem'}}>
      <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
        <button onClick={onBack} style={{background:'rgba(255,255,255,0.1)',border:'none',color:'white',padding:'6px 14px',borderRadius:'10px',cursor:'pointer',fontSize:'13px'}}>← Volver</button>
        <span style={{color:'white',fontWeight:'700',fontSize:'16px'}}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  // ─── PANEL ARIA ───────────────────────────────────────────────
  const PanelARIA = () => {
    if (!panelARIA) return null;
    const col = panelARIA==='revision'?'#3b82f6':panelARIA==='simplificar'?'#22c55e':panelARIA==='vencidos'?'#f59e0b':panelARIA==='nc_vinculacion'?'#e63946':'#7B5BF2';

    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
        <div style={{background:cardBg,borderRadius:'16px',padding:'2rem',maxWidth:'620px',width:'100%',border:`2px solid ${col}`,maxHeight:'85vh',overflowY:'auto'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
            <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
              <span style={{fontSize:'24px'}}>🤖</span>
              <div>
                <p style={{fontSize:'14px',fontWeight:'800',color:col,margin:0}}>ARIA — Asistente POEs</p>
                <p style={{fontSize:'11px',color:sub,margin:0}}>
                  {panelARIA==='pasos'&&'Generación automática de pasos'}
                  {panelARIA==='revision'&&'Revisión de consistencia del POE'}
                  {panelARIA==='simplificar'&&'Simplificación de lenguaje para operarios'}
                  {panelARIA==='vencidos'&&'Análisis de POEs vencidos'}
                  {panelARIA==='nc_vinculacion'&&'Vinculación con No Conformidades'}
                </p>
              </div>
            </div>
            <button onClick={()=>{setPanelARIA(null);setResultadoARIA(null);}} style={{background:'none',border:'none',color:sub,cursor:'pointer',fontSize:'20px'}}>×</button>
          </div>

          {/* Formulario para generar pasos */}
          {panelARIA==='pasos'&&!resultadoARIA&&!cargandoARIA&&(
            <div>
              <p style={{fontSize:'13px',color:sub,marginBottom:'12px'}}>Describí el procedimiento en lenguaje natural y ARIA generará los pasos estructurados.</p>
              <textarea rows={5} value={descripcionARIA} onChange={e=>setDescripcionARIA(e.target.value)}
                style={{...inp,resize:'none'}}
                placeholder="Ej: Procedimiento para recibir materiales del proveedor, verificar que coincidan con la orden de compra, controlar calidad visual, registrar en el sistema y ubicar en el depósito correspondiente según categoría de material..."/>
              <button onClick={ariaGenerarPasos} disabled={cargandoARIA||!descripcionARIA.trim()} style={{...btn(col),width:'100%',marginTop:'12px',padding:'12px'}}>
                🤖 Generar pasos automáticamente
              </button>
            </div>
          )}

          {cargandoARIA&&(
            <div style={{textAlign:'center',padding:'3rem'}}>
              <div style={{fontSize:'40px',marginBottom:'1rem'}}>⚙️</div>
              <p style={{color:col,fontWeight:'700',fontSize:'14px'}}>ARIA está procesando...</p>
            </div>
          )}

          {!cargandoARIA&&resultadoARIA&&!resultadoARIA.error&&(
            <div>
              {/* PASOS GENERADOS */}
              {panelARIA==='pasos'&&Array.isArray(resultadoARIA)&&(
                <div>
                  <p style={{fontSize:'13px',color:sub,marginBottom:'12px'}}>ARIA generó {resultadoARIA.length} pasos. Revisalos y confirmalos.</p>
                  {resultadoARIA.map((p,i)=>(
                    <div key={i} style={{display:'flex',gap:'12px',padding:'10px',borderRadius:'10px',background:oscuro?'#091840':'#f8fafc',border:`1px solid ${borde}`,marginBottom:'8px'}}>
                      <div style={{width:'28px',height:'28px',borderRadius:'50%',background:Q.azulVivo,color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',fontWeight:'800',flexShrink:0}}>{i+1}</div>
                      <div style={{flex:1}}>
                        <p style={{fontSize:'13px',color:texto,fontWeight:'600',margin:'0 0 4px'}}>{p.descripcion}</p>
                        {p.responsable&&<p style={{fontSize:'11px',color:sub,margin:'0 0 2px'}}>👤 {p.responsable}</p>}
                        {p.criterioAceptacion&&<p style={{fontSize:'11px',color:'#22c55e',margin:'0 0 2px'}}>✅ {p.criterioAceptacion}</p>}
                        {p.advertencia&&<p style={{fontSize:'11px',color:'#f59e0b',margin:0}}>⚠️ {p.advertencia}</p>}
                      </div>
                    </div>
                  ))}
                  <button onClick={aplicarPasosARIA} style={{...btn(col),width:'100%',marginTop:'12px',padding:'12px'}}>
                    ✅ Agregar {resultadoARIA.length} pasos al POE
                  </button>
                </div>
              )}

              {/* REVISIÓN */}
              {panelARIA==='revision'&&resultadoARIA.puntuacion!==undefined&&(
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:'1rem',padding:'1rem',borderRadius:'12px',background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #3b82f640',marginBottom:'1rem'}}>
                    <div style={{textAlign:'center',minWidth:'64px'}}>
                      <p style={{fontSize:'36px',fontWeight:'900',color:resultadoARIA.puntuacion>=80?'#22c55e':resultadoARIA.puntuacion>=60?'#f59e0b':'#e63946',margin:0}}>{resultadoARIA.puntuacion}</p>
                      <p style={{fontSize:'10px',color:sub,margin:0}}>/ 100</p>
                    </div>
                    <p style={{fontSize:'13px',color:texto,margin:0}}>{resultadoARIA.recomendacionGeneral}</p>
                  </div>
                  {resultadoARIA.problemas?.length>0&&(
                    <div style={{marginBottom:'1rem'}}>
                      <p style={{fontSize:'12px',fontWeight:'700',color:'#e63946',margin:'0 0 8px'}}>⚠️ Problemas detectados:</p>
                      {resultadoARIA.problemas.map((prob,i)=>(
                        <div key={i} style={{padding:'8px 12px',borderRadius:'8px',background:oscuro?'#1a0505':'#fff5f5',border:'1px solid #e6394640',marginBottom:'6px'}}>
                          <p style={{fontSize:'12px',fontWeight:'600',color:'#e63946',margin:'0 0 2px'}}>Paso {prob.paso} — {prob.tipo}</p>
                          <p style={{fontSize:'11px',color:texto,margin:'0 0 2px'}}>{prob.descripcion}</p>
                          <p style={{fontSize:'11px',color:'#22c55e',margin:0}}>💡 {prob.sugerencia}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {resultadoARIA.faltantes?.length>0&&(
                    <div style={{marginBottom:'1rem'}}>
                      <p style={{fontSize:'12px',fontWeight:'700',color:'#f59e0b',margin:'0 0 6px'}}>📋 Elementos faltantes:</p>
                      {resultadoARIA.faltantes.map((f,i)=><p key={i} style={{fontSize:'11px',color:texto,margin:'2px 0'}}>• {f}</p>)}
                    </div>
                  )}
                  {resultadoARIA.fortalezas?.length>0&&(
                    <div>
                      <p style={{fontSize:'12px',fontWeight:'700',color:'#22c55e',margin:'0 0 6px'}}>✅ Fortalezas:</p>
                      {resultadoARIA.fortalezas.map((f,i)=><p key={i} style={{fontSize:'11px',color:texto,margin:'2px 0'}}>• {f}</p>)}
                    </div>
                  )}
                </div>
              )}

              {/* SIMPLIFICACIÓN */}
              {panelARIA==='simplificar'&&Array.isArray(resultadoARIA)&&(
                <div>
                  <p style={{fontSize:'13px',color:sub,marginBottom:'12px'}}>Comparativo original vs versión simplificada:</p>
                  {resultadoARIA.map((item,i)=>(
                    <div key={i} style={{padding:'10px',borderRadius:'10px',background:oscuro?'#091840':'#f8fafc',border:`1px solid ${borde}`,marginBottom:'8px'}}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                        <div>
                          <p style={{fontSize:'10px',color:sub,fontWeight:'700',margin:'0 0 4px',textTransform:'uppercase'}}>Original</p>
                          <p style={{fontSize:'12px',color:sub,margin:0,fontStyle:'italic'}}>{item.original}</p>
                        </div>
                        <div>
                          <p style={{fontSize:'10px',color:'#22c55e',fontWeight:'700',margin:'0 0 4px',textTransform:'uppercase'}}>Simplificado</p>
                          <p style={{fontSize:'12px',color:texto,margin:0,fontWeight:'600'}}>{item.simplificado}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button onClick={aplicarSimplificacion} style={{...btn('#22c55e'),width:'100%',marginTop:'12px',padding:'12px'}}>
                    ✅ Aplicar versión simplificada
                  </button>
                </div>
              )}

              {/* VENCIDOS */}
              {panelARIA==='vencidos'&&resultadoARIA.resumen&&(
                <div>
                  <div style={{padding:'12px',borderRadius:'10px',background:oscuro?'#1a1000':'#fffbeb',border:'1px solid #f59e0b40',marginBottom:'1rem'}}>
                    <p style={{fontSize:'13px',color:texto,margin:'0 0 4px',fontWeight:'600'}}>{resultadoARIA.resumen}</p>
                    <p style={{fontSize:'12px',color:sub,margin:0}}>{resultadoARIA.recomendacionGeneral}</p>
                  </div>
                  {resultadoARIA.prioridades?.map((p,i)=>(
                    <div key={i} style={{padding:'10px 14px',borderRadius:'10px',border:`1px solid ${p.urgencia==='alta'?'#e63946':p.urgencia==='media'?'#f59e0b':'#22c55e'}40`,background:cardBg,marginBottom:'8px'}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                        <span style={{fontSize:'12px',fontWeight:'700',color:texto}}>{p.codigo} — {p.titulo}</span>
                        <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'6px',background:p.urgencia==='alta'?'#fee2e2':p.urgencia==='media'?'#fef9c3':'#dcfce7',color:p.urgencia==='alta'?'#b91c1c':p.urgencia==='media'?'#a16207':'#15803d'}}>{p.urgencia?.toUpperCase()}</span>
                      </div>
                      <p style={{fontSize:'11px',color:sub,margin:'0 0 2px'}}>{p.razon}</p>
                      <p style={{fontSize:'11px',color:'#22c55e',margin:0}}>💡 {p.accionRecomendada}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* VINCULACIÓN NC */}
              {panelARIA==='nc_vinculacion'&&Array.isArray(resultadoARIA)&&(
                <div>
                  <p style={{fontSize:'13px',color:sub,marginBottom:'12px'}}>ARIA encontró {resultadoARIA.length} relaciones entre NCs y POEs:</p>
                  {resultadoARIA.map((r,i)=>(
                    <div key={i} style={{padding:'10px 14px',borderRadius:'10px',border:'1px solid #e6394640',background:oscuro?'#1a0505':'#fff5f5',marginBottom:'8px'}}>
                      <div style={{display:'flex',gap:'8px',marginBottom:'4px',flexWrap:'wrap'}}>
                        <span style={{fontSize:'11px',fontWeight:'700',color:'#e63946',background:'#fee2e2',padding:'2px 7px',borderRadius:'5px'}}>{r.nc}</span>
                        <span style={{fontSize:'11px',color:sub}}>→</span>
                        <span style={{fontSize:'11px',fontWeight:'700',color:Q.azulVivo,background:`${Q.azulVivo}15`,padding:'2px 7px',borderRadius:'5px'}}>{r.poe}</span>
                        <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 7px',borderRadius:'5px',background:r.accion==='crear nuevo'?'#fef9c3':'#dcfce7',color:r.accion==='crear nuevo'?'#a16207':'#15803d'}}>{r.accion}</span>
                      </div>
                      <p style={{fontSize:'11px',color:texto,margin:0}}>{r.relacion}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!cargandoARIA&&resultadoARIA?.error&&(
            <div style={{textAlign:'center',padding:'2rem'}}>
              <p style={{fontSize:'32px'}}>❌</p>
              <p style={{color:'#e63946',fontWeight:'700'}}>{resultadoARIA.error}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════
  // LECTURA / FIRMA
  // ════════════════════════════════════════════════════════════
  if (vista==='lectura'&&poeActual) return (
    <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
      <Header titulo={`📖 Lectura — ${poeActual.codigo}`} onBack={()=>setVista('detalle')}/>
      <div style={{padding:'0 2rem 2rem',maxWidth:'700px',margin:'0 auto'}}>
        <div style={{...card,background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #2A7FFF40'}}>
          <h2 style={{fontSize:'18px',fontWeight:'800',color:texto,margin:'0 0 4px'}}>{poeActual.titulo}</h2>
          <p style={{fontSize:'12px',color:sub,margin:'0 0 12px'}}>{poeActual.codigo} · v{poeActual.version} · {poeActual.categoria}</p>
          {poeActual.objetivo&&<><p style={{fontSize:'11px',color:sub,fontWeight:'700',margin:'0 0 2px',textTransform:'uppercase'}}>Objetivo</p><p style={{fontSize:'13px',color:texto,margin:'0 0 12px'}}>{poeActual.objetivo}</p></>}
          {poeActual.alcance&&<><p style={{fontSize:'11px',color:sub,fontWeight:'700',margin:'0 0 2px',textTransform:'uppercase'}}>Alcance</p><p style={{fontSize:'13px',color:texto}}>{poeActual.alcance}</p></>}
        </div>
        {(poeActual.pasos||[]).length>0&&(
          <div style={card}>
            <h3 style={{fontSize:'14px',fontWeight:'700',color:Q.azul,marginBottom:'14px'}}>Pasos del procedimiento</h3>
            {(poeActual.pasos||[]).map((paso,i)=>(
              <div key={paso.id} style={{display:'flex',gap:'14px',marginBottom:'16px',paddingBottom:'16px',borderBottom:i<(poeActual.pasos.length-1)?`1px solid ${borde}`:'none'}}>
                <div style={{width:'36px',height:'36px',borderRadius:'50%',background:Q.azulVivo,color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',fontWeight:'800',flexShrink:0}}>{i+1}</div>
                <div style={{flex:1}}>
                  <p style={{fontSize:'14px',color:texto,fontWeight:'600',margin:'0 0 4px'}}>{paso.descripcion}</p>
                  {paso.descripcionOriginal&&<p style={{fontSize:'10px',color:sub,margin:'0 0 4px',fontStyle:'italic'}}>Original: {paso.descripcionOriginal}</p>}
                  {paso.responsable&&<p style={{fontSize:'11px',color:sub,margin:'0 0 2px'}}>👤 {paso.responsable}</p>}
                  {paso.criterioAceptacion&&<p style={{fontSize:'11px',color:'#22c55e',margin:'0 0 2px'}}>✅ {paso.criterioAceptacion}</p>}
                  {paso.advertencia&&<div style={{background:'#fffbeb',border:'1px solid #f59e0b',borderRadius:'8px',padding:'6px 10px',marginTop:'6px'}}><p style={{fontSize:'11px',color:'#f59e0b',fontWeight:'700',margin:0}}>⚠️ {paso.advertencia}</p></div>}
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{...card,background:oscuro?'#0a1f0a':'#f0fdf4',border:'2px solid #22c55e40'}}>
          <h3 style={{fontSize:'14px',fontWeight:'700',color:'#22c55e',marginBottom:'12px'}}>✍️ Confirmación de lectura</h3>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <div><span style={lbl}>Nombre *</span><input value={formLectura.operador} onChange={e=>setFormLectura(f=>({...f,operador:e.target.value}))} style={inp} placeholder={usuario?.nombre}/></div>
            <div><span style={lbl}>Legajo</span><input value={formLectura.legajo} onChange={e=>setFormLectura(f=>({...f,legajo:e.target.value}))} style={inp}/></div>
            <div style={{gridColumn:'1/-1'}}><span style={lbl}>Observaciones</span><input value={formLectura.observaciones} onChange={e=>setFormLectura(f=>({...f,observaciones:e.target.value}))} style={inp}/></div>
          </div>
          <button onClick={registrarLectura} style={{...btn('#22c55e'),marginTop:'14px',width:'100%',padding:'12px',fontSize:'14px'}}>✅ Confirmo haber leído y comprendido</button>
        </div>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════
  // DETALLE
  // ════════════════════════════════════════════════════════════
  if (vista==='detalle'&&poeActual) {
    const pasos    = poeActual.pasos||[];
    const lecturas = poeActual.lecturas||[];
    const histVer  = poeActual.historialVersiones||[];
    const estadoInfo = ESTADOS_POE[poeActual.estado]||{};

    const secciones = [
      {id:'info',    label:'📋 Info'},
      {id:'pasos',   label:`🔢 Pasos (${pasos.length})`},
      {id:'lecturas',label:`✍️ Lecturas (${lecturas.length})`},
      {id:'historial',label:'📅 Versiones'},
    ];

    return (
      <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
        <PanelARIA/>
        <Header
          titulo={`${poeActual.codigo} — ${poeActual.titulo}`}
          onBack={()=>{setVista('listado');setPoeActual(null);}}
          extra={
            <div style={{display:'flex',gap:'6px',alignItems:'center',flexWrap:'wrap'}}>
              {esAdmin&&(
                <select value={poeActual.estado} onChange={e=>actualizarPoe({estado:e.target.value})}
                  style={{padding:'5px 10px',borderRadius:'8px',border:`1px solid ${estadoInfo.color}`,background:`${estadoInfo.color}22`,color:estadoInfo.color,fontSize:'11px',fontWeight:'700',cursor:'pointer'}}>
                  {Object.entries(ESTADOS_POE).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                </select>
              )}
              {/* Botones ARIA */}
              <button onClick={()=>{setPanelARIA('pasos');setResultadoARIA(null);setDescripcionARIA('');}} disabled={cargandoARIA}
                style={{padding:'5px 10px',background:'linear-gradient(135deg,#7B5BF2,#1C3FAA)',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'11px',color:'white',fontWeight:'600'}}>
                🤖 Generar pasos
              </button>
              <button onClick={ariaRevisarPOE} disabled={cargandoARIA}
                style={{padding:'5px 10px',background:'rgba(59,130,246,.2)',border:'1px solid rgba(59,130,246,.5)',borderRadius:'8px',cursor:'pointer',fontSize:'11px',color:'#3b82f6',fontWeight:'600'}}>
                🔍 Revisar
              </button>
              <button onClick={ariaSimplificar} disabled={cargandoARIA}
                style={{padding:'5px 10px',background:'rgba(34,197,94,.2)',border:'1px solid rgba(34,197,94,.4)',borderRadius:'8px',cursor:'pointer',fontSize:'11px',color:'#22c55e',fontWeight:'600'}}>
                📝 Simplificar
              </button>
              <button onClick={()=>setVista('lectura')}
                style={{padding:'5px 10px',background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',color:'white',borderRadius:'8px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>
                📖 Leer y firmar
              </button>
              {esAdmin&&(
                <>
                  <button onClick={nuevaVersionPoe} style={{padding:'5px 10px',background:'rgba(59,130,246,.2)',border:'1px solid rgba(59,130,246,.4)',color:'#3b82f6',borderRadius:'8px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>🔄 v{nuevaVersion(poeActual.version)}</button>
                  <button onClick={()=>setConfirmEliminar(poeActual.id)} style={{padding:'5px 10px',background:'rgba(230,57,70,.2)',border:'1px solid rgba(230,57,70,.4)',color:'#e63946',borderRadius:'8px',cursor:'pointer',fontSize:'11px'}}>🗑</button>
                </>
              )}
            </div>
          }
        />

        <div style={{padding:'0 2rem 2rem',maxWidth:'860px',margin:'0 auto'}}>
          <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'1rem',flexWrap:'wrap'}}>
            <span style={{fontSize:'12px',fontWeight:'700',padding:'3px 12px',borderRadius:'8px',background:`${estadoInfo.color}22`,color:estadoInfo.color}}>{estadoInfo.label}</span>
            <span style={{fontSize:'12px',color:sub}}>v{poeActual.version}</span>
            <span style={{fontSize:'12px',color:sub}}>· Mod: {poeActual.ultimaModificacion?.slice(0,10)}</span>
            {(poeActual.modulos||[]).map(m=>{
              const mod=MODULOS_ALQOS.find(x=>x.id===m);
              return mod?<span key={m} style={{fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'6px',background:`${Q.azulVivo}15`,color:Q.azulVivo}}>{mod.icon} {mod.label}</span>:null;
            })}
          </div>

          <div style={{display:'flex',gap:'4px',marginBottom:'1.5rem',overflowX:'auto'}}>
            {secciones.map(s=>(
              <button key={s.id} onClick={()=>setSeccionActiva(s.id)}
                style={{padding:'7px 14px',borderRadius:'8px',border:'none',cursor:'pointer',fontSize:'12px',fontWeight:'700',whiteSpace:'nowrap',background:seccionActiva===s.id?Q.azulVivo:'transparent',color:seccionActiva===s.id?'white':sub,flexShrink:0}}>
                {s.label}
              </button>
            ))}
          </div>

          {/* INFO */}
          {seccionActiva==='info'&&(
            <div style={card}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <div><span style={lbl}>Título</span><input value={poeActual.titulo} onChange={e=>actualizarPoe({titulo:e.target.value})} style={inp}/></div>
                <div><span style={lbl}>Categoría</span><select value={poeActual.categoria} onChange={e=>actualizarPoe({categoria:e.target.value})} style={inp}>{CATEGORIAS.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
                <div><span style={lbl}>Responsable</span><input value={poeActual.responsable||''} onChange={e=>actualizarPoe({responsable:e.target.value})} style={inp}/></div>
                <div><span style={lbl}>Frecuencia revisión</span><select value={poeActual.frecuenciaRevision||'Anual'} onChange={e=>actualizarPoe({frecuenciaRevision:e.target.value})} style={inp}>{['Mensual','Trimestral','Semestral','Anual','Cuando haya cambios'].map(f=><option key={f} value={f}>{f}</option>)}</select></div>
                <div style={{gridColumn:'1/-1'}}><span style={lbl}>Objetivo</span><textarea rows={2} value={poeActual.objetivo||''} onChange={e=>actualizarPoe({objetivo:e.target.value})} style={{...inp,resize:'none'}}/></div>
                <div style={{gridColumn:'1/-1'}}><span style={lbl}>Alcance</span><textarea rows={2} value={poeActual.alcance||''} onChange={e=>actualizarPoe({alcance:e.target.value})} style={{...inp,resize:'none'}}/></div>
                <div style={{gridColumn:'1/-1'}}>
                  <span style={lbl}>Módulos ALQOS™ vinculados</span>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginTop:'8px'}}>
                    {MODULOS_ALQOS.map(m=>{
                      const activo=(poeActual.modulos||[]).includes(m.id);
                      return (
                        <button key={m.id} onClick={()=>{const mods=poeActual.modulos||[];actualizarPoe({modulos:activo?mods.filter(x=>x!==m.id):[...mods,m.id]});}}
                          style={{padding:'5px 12px',borderRadius:'20px',fontSize:'11px',cursor:'pointer',fontWeight:'600',border:`1.5px solid ${activo?Q.azulVivo:borde}`,background:activo?`${Q.azulVivo}15`:cardBg,color:activo?Q.azulVivo:sub}}>
                          {m.icon} {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PASOS */}
          {seccionActiva==='pasos'&&(
            <div>
              {/* Banner ARIA */}
              <div style={{...card,background:oscuro?'#0a0a2a':'#f5f3ff',border:'2px solid #7B5BF2',marginBottom:'1rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <p style={{fontSize:'13px',fontWeight:'700',color:'#7B5BF2',margin:'0 0 2px'}}>🤖 ARIA — Generar pasos automáticamente</p>
                  <p style={{fontSize:'11px',color:sub,margin:0}}>Describí el procedimiento y ARIA genera los pasos estructurados</p>
                </div>
                <button onClick={()=>{setPanelARIA('pasos');setResultadoARIA(null);setDescripcionARIA('');}}
                  style={{...btn('#7B5BF2'),whiteSpace:'nowrap',flexShrink:0}}>🤖 Generar</button>
              </div>

              {esAdmin&&(
                <div style={{...card,background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #2A7FFF40'}}>
                  <h3 style={{fontSize:'13px',fontWeight:'700',color:Q.azulVivo,marginBottom:'12px'}}>+ Paso manual</h3>
                  <span style={lbl}>Descripción *</span>
                  <textarea rows={2} value={formPaso.descripcion} onChange={e=>setFormPaso(f=>({...f,descripcion:e.target.value}))} style={{...inp,resize:'none'}}/>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                    <div><span style={lbl}>Responsable</span><input value={formPaso.responsable} onChange={e=>setFormPaso(f=>({...f,responsable:e.target.value}))} style={inp}/></div>
                    <div><span style={lbl}>Criterio de aceptación</span><input value={formPaso.criterioAceptacion} onChange={e=>setFormPaso(f=>({...f,criterioAceptacion:e.target.value}))} style={inp}/></div>
                    <div style={{gridColumn:'1/-1'}}><span style={lbl}>⚠️ Advertencia</span><input value={formPaso.advertencia} onChange={e=>setFormPaso(f=>({...f,advertencia:e.target.value}))} style={inp}/></div>
                  </div>
                  <button onClick={agregarPaso} style={{...btn(),marginTop:'12px'}}>+ Agregar paso</button>
                </div>
              )}

              {pasos.length===0?(
                <div style={{textAlign:'center',padding:'3rem',color:sub}}><p style={{fontSize:'32px'}}>🔢</p><p>Sin pasos. Usá ARIA para generarlos.</p></div>
              ):pasos.map((paso,i)=>(
                <div key={paso.id} style={{...card,borderLeft:`4px solid ${paso.advertencia?'#f59e0b':Q.azulVivo}`}}>
                  <div style={{display:'flex',gap:'12px',alignItems:'flex-start'}}>
                    <div style={{width:'36px',height:'36px',borderRadius:'50%',background:Q.azulVivo,color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',fontWeight:'800',flexShrink:0}}>{i+1}</div>
                    <div style={{flex:1}}>
                      {editandoPaso===paso.id?(
                        <div>
                          <textarea rows={2} defaultValue={paso.descripcion} id={`desc_${paso.id}`} style={{...inp,resize:'none',marginTop:0}}/>
                          <div style={{display:'flex',gap:'8px',marginTop:'8px'}}>
                            <button onClick={()=>{const desc=document.getElementById(`desc_${paso.id}`)?.value||paso.descripcion;actualizarPoe({pasos:pasos.map(p=>p.id===paso.id?{...p,descripcion:desc}:p)});setEditandoPaso(null);}} style={btn()}>Guardar</button>
                            <button onClick={()=>setEditandoPaso(null)} style={{...btn('#8E93A1')}}>Cancelar</button>
                          </div>
                        </div>
                      ):(
                        <>
                          <div style={{display:'flex',gap:'6px',marginBottom:'4px'}}>
                            {paso.origenARIA&&<span style={{fontSize:'9px',fontWeight:'700',background:'linear-gradient(135deg,#7B5BF2,#1C3FAA)',color:'white',padding:'1px 6px',borderRadius:'4px'}}>ARIA</span>}
                            {paso.simplificadoPorARIA&&<span style={{fontSize:'9px',fontWeight:'700',background:'#dcfce7',color:'#15803d',padding:'1px 6px',borderRadius:'4px'}}>SIMPLIFICADO</span>}
                          </div>
                          <p style={{fontSize:'14px',color:texto,fontWeight:'600',margin:'0 0 6px'}}>{paso.descripcion}</p>
                          {paso.descripcionOriginal&&<p style={{fontSize:'10px',color:sub,margin:'0 0 4px',fontStyle:'italic'}}>Original: {paso.descripcionOriginal}</p>}
                          {paso.responsable&&<p style={{fontSize:'11px',color:sub,margin:'0 0 2px'}}>👤 {paso.responsable}</p>}
                          {paso.criterioAceptacion&&<p style={{fontSize:'11px',color:'#22c55e',margin:'0 0 2px'}}>✅ {paso.criterioAceptacion}</p>}
                          {paso.advertencia&&<div style={{background:oscuro?'#1a1000':'#fffbeb',border:'1px solid #f59e0b',borderRadius:'8px',padding:'6px 10px',marginTop:'6px'}}><p style={{fontSize:'11px',color:'#f59e0b',fontWeight:'700',margin:0}}>⚠️ {paso.advertencia}</p></div>}
                        </>
                      )}
                    </div>
                    {esAdmin&&(
                      <div style={{display:'flex',flexDirection:'column',gap:'4px',flexShrink:0}}>
                        <button onClick={()=>moverPaso(paso.id,'up')} style={{background:'none',border:`1px solid ${borde}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px',padding:'3px 7px',color:sub}}>↑</button>
                        <button onClick={()=>moverPaso(paso.id,'down')} style={{background:'none',border:`1px solid ${borde}`,borderRadius:'6px',cursor:'pointer',fontSize:'12px',padding:'3px 7px',color:sub}}>↓</button>
                        <button onClick={()=>setEditandoPaso(paso.id)} style={{background:'none',border:'none',cursor:'pointer',fontSize:'13px',color:Q.azulVivo}}>✏️</button>
                        <button onClick={()=>actualizarPoe({pasos:pasos.filter(p=>p.id!==paso.id)})} style={{background:'none',border:'none',cursor:'pointer',fontSize:'13px',color:'#e63946'}}>×</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div style={card}>
                <h3 style={{fontSize:'13px',fontWeight:'700',color:Q.azul,marginBottom:'10px'}}>🔧 Materiales</h3>
                {esAdmin&&(
                  <div style={{display:'flex',gap:'8px',marginBottom:'10px'}}>
                    <input value={formMaterial} onChange={e=>setFormMaterial(e.target.value)}
                      onKeyDown={e=>{if(e.key==='Enter'&&formMaterial.trim()){actualizarPoe({materiales:[...(poeActual.materiales||[]),formMaterial.trim()]});setFormMaterial('');}}}
                      style={{...inp,marginTop:0,flex:1}} placeholder="Ej: Guantes de nitrilo, planilla..."/>
                    <button onClick={()=>{if(formMaterial.trim()){actualizarPoe({materiales:[...(poeActual.materiales||[]),formMaterial.trim()]});setFormMaterial('');}}} style={btn()}>+</button>
                  </div>
                )}
                {(poeActual.materiales||[]).length===0?<p style={{fontSize:'12px',color:sub}}>Sin materiales</p>:(poeActual.materiales||[]).map((m,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'5px 0',borderBottom:`0.5px solid ${borde}`}}>
                    <span style={{fontSize:'13px',color:texto}}>• {m}</span>
                    {esAdmin&&<button onClick={()=>actualizarPoe({materiales:(poeActual.materiales||[]).filter((_,j)=>j!==i)})} style={{background:'none',border:'none',color:'#e63946',cursor:'pointer',fontSize:'14px'}}>×</button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* LECTURAS */}
          {seccionActiva==='lecturas'&&(
            <div>
              <div style={{...card,background:oscuro?'#0a1f0a':'#f0fdf4',border:'1px solid #22c55e40',textAlign:'center',padding:'1.5rem'}}>
                <p style={{fontSize:'28px',fontWeight:'800',color:'#22c55e',margin:'0 0 4px'}}>{lecturas.length}</p>
                <p style={{fontSize:'12px',color:sub,margin:'0 0 12px'}}>Operadores que leyeron v{poeActual.version}</p>
                <button onClick={()=>setVista('lectura')} style={btn('#22c55e')}>📖 Registrar mi lectura</button>
              </div>
              {lecturas.length===0?(
                <div style={{textAlign:'center',padding:'2rem',color:sub}}><p>Sin lecturas para esta versión</p></div>
              ):[...lecturas].reverse().map(l=>(
                <div key={l.id} style={{...card,borderLeft:'4px solid #22c55e'}}>
                  <p style={{fontSize:'13px',fontWeight:'700',color:texto,margin:'0 0 2px'}}>✅ {l.operador}</p>
                  <p style={{fontSize:'11px',color:sub,margin:0}}>{l.legajo&&`Legajo: ${l.legajo} · `}v{l.version} · {new Date(l.fecha).toLocaleDateString('es-AR')} {new Date(l.fecha).toLocaleTimeString('es-AR',{hour:'2-digit',minute:'2-digit'})}</p>
                  {l.observaciones&&<p style={{fontSize:'11px',color:sub,margin:'4px 0 0',fontStyle:'italic'}}>"{l.observaciones}"</p>}
                </div>
              ))}
            </div>
          )}

          {/* HISTORIAL */}
          {seccionActiva==='historial'&&(
            <div>
              <div style={{...card,background:oscuro?'#0a1f5c':'#eff6ff',border:'1px solid #2A7FFF40',borderLeft:'4px solid #2A7FFF'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <p style={{fontSize:'13px',fontWeight:'700',color:texto,margin:'0 0 2px'}}>v{poeActual.version} — Versión actual</p>
                    <p style={{fontSize:'11px',color:sub,margin:0}}>{poeActual.ultimaModificacion?.slice(0,10)} · {poeActual.modificadoPor||poeActual.creadoPor}</p>
                  </div>
                  <span style={{fontSize:'11px',fontWeight:'700',background:`${estadoInfo.color}22`,color:estadoInfo.color,padding:'3px 10px',borderRadius:'8px'}}>{estadoInfo.label}</span>
                </div>
              </div>
              {histVer.length===0?<p style={{fontSize:'13px',color:sub,textAlign:'center',padding:'1.5rem'}}>Sin versiones anteriores</p>:histVer.slice().reverse().map(h=>(
                <div key={h.version} style={{...card,opacity:0.7}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div>
                      <p style={{fontSize:'13px',fontWeight:'600',color:texto,margin:'0 0 2px'}}>v{h.version}</p>
                      <p style={{fontSize:'11px',color:sub,margin:0}}>{h.fecha?.slice(0,10)} · {h.usuario||'—'} · {h.pasos?.length||0} pasos</p>
                    </div>
                    <span style={{fontSize:'10px',fontWeight:'700',background:`${ESTADOS_POE[h.estado]?.color}22`,color:ESTADOS_POE[h.estado]?.color,padding:'2px 8px',borderRadius:'6px'}}>{ESTADOS_POE[h.estado]?.label||h.estado}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // NUEVO
  if (vista==='nuevo') return (
    <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
      <Header titulo="Nuevo POE" onBack={()=>setVista('listado')}/>
      <div style={{padding:'0 2rem 2rem',maxWidth:'700px',margin:'0 auto'}}>
        <div style={card}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <div style={{gridColumn:'1/-1'}}><span style={lbl}>Título *</span><input value={form.titulo} onChange={e=>setForm(f=>({...f,titulo:e.target.value}))} style={inp} placeholder="Ej: POE de control de calidad en recepción de materiales"/></div>
            <div><span style={lbl}>Código (opcional)</span><input value={form.codigo} onChange={e=>setForm(f=>({...f,codigo:e.target.value}))} style={inp} placeholder={generarCodigo(poes)}/></div>
            <div><span style={lbl}>Categoría</span><select value={form.categoria} onChange={e=>setForm(f=>({...f,categoria:e.target.value}))} style={inp}>{CATEGORIAS.map(c=><option key={c} value={c}>{c}</option>)}</select></div>
            <div><span style={lbl}>Responsable</span><input value={form.responsable} onChange={e=>setForm(f=>({...f,responsable:e.target.value}))} style={inp}/></div>
            <div><span style={lbl}>Frecuencia revisión</span><select value={form.frecuenciaRevision} onChange={e=>setForm(f=>({...f,frecuenciaRevision:e.target.value}))} style={inp}>{['Mensual','Trimestral','Semestral','Anual','Cuando haya cambios'].map(f=><option key={f} value={f}>{f}</option>)}</select></div>
            <div style={{gridColumn:'1/-1'}}><span style={lbl}>Objetivo</span><textarea rows={2} value={form.objetivo} onChange={e=>setForm(f=>({...f,objetivo:e.target.value}))} style={{...inp,resize:'none'}}/></div>
            <div style={{gridColumn:'1/-1'}}>
              <span style={lbl}>Módulos ALQOS™ vinculados</span>
              <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginTop:'8px'}}>
                {MODULOS_ALQOS.map(m=>{
                  const activo=form.modulos.includes(m.id);
                  return <button key={m.id} onClick={()=>setForm(f=>({...f,modulos:activo?f.modulos.filter(x=>x!==m.id):[...f.modulos,m.id]}))} style={{padding:'5px 12px',borderRadius:'20px',fontSize:'11px',cursor:'pointer',fontWeight:'600',border:`1.5px solid ${activo?Q.azulVivo:borde}`,background:activo?`${Q.azulVivo}15`:cardBg,color:activo?Q.azulVivo:sub}}>{m.icon} {m.label}</button>;
                })}
              </div>
            </div>
          </div>
          <button onClick={crearPoe} style={{...btn(),marginTop:'1.5rem',width:'100%',padding:'14px',fontSize:'14px'}}>📝 Crear POE y cargar pasos</button>
        </div>
      </div>
    </div>
  );

  // CONFIRMAR ELIMINAR
  if (confirmEliminar) return (
    <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:cardBg,borderRadius:'16px',padding:'2rem',maxWidth:'360px',width:'90%',border:`1px solid ${borde}`,textAlign:'center'}}>
        <p style={{fontSize:'36px',marginBottom:'1rem'}}>📄</p>
        <p style={{fontSize:'15px',fontWeight:'600',color:texto,marginBottom:'.5rem'}}>¿Eliminar este POE?</p>
        <p style={{fontSize:'13px',color:sub,marginBottom:'1.5rem'}}>Esta acción no se puede deshacer.</p>
        <div style={{display:'flex',gap:'10px',justifyContent:'center'}}>
          <button onClick={()=>setConfirmEliminar(null)} style={{padding:'10px 20px',background:'none',border:`1px solid ${borde}`,borderRadius:'10px',cursor:'pointer',color:texto,fontSize:'13px'}}>Cancelar</button>
          <button onClick={()=>eliminarPoe(confirmEliminar)} style={{padding:'10px 20px',background:'#e63946',border:'none',borderRadius:'10px',cursor:'pointer',color:'white',fontSize:'13px',fontWeight:'600'}}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  // LISTADO
  return (
    <div style={{minHeight:'100vh',background:bg,fontFamily:'sans-serif'}}>
      <div style={{background:navBg,padding:'1rem 1.5rem',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100,boxShadow:'0 2px 12px rgba(0,0,0,.3)'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <button onClick={onVolver} style={{background:'rgba(255,255,255,0.1)',border:'none',color:'white',padding:'6px 14px',borderRadius:'10px',cursor:'pointer',fontSize:'13px'}}>← Menú</button>
          <BotonInstructivo moduloKey="poes" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="poes" oscuro={oscuro} />}
          <span style={{color:'white',fontWeight:'700',fontSize:'16px'}}>📄 POEs</span>
          {alertasVencidos.length>0&&<span style={{background:'#f59e0b',color:'white',fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'10px'}}>⚠️ {alertasVencidos.length} vencidos</span>}
        </div>
        <div style={{display:'flex',gap:'8px'}}>
          {alertasVencidos.length>0&&(
            <button onClick={ariaAlertaVencidos} disabled={cargandoARIA}
              style={{padding:'6px 12px',background:'rgba(245,158,11,.2)',border:'1px solid rgba(245,158,11,.5)',borderRadius:'8px',cursor:'pointer',fontSize:'11px',color:'#f59e0b',fontWeight:'600'}}>
              🤖 Analizar vencidos
            </button>
          )}
          <button onClick={ariaVinculacionNC} disabled={cargandoARIA}
            style={{padding:'6px 12px',background:'rgba(230,57,70,.2)',border:'1px solid rgba(230,57,70,.4)',borderRadius:'8px',cursor:'pointer',fontSize:'11px',color:'#e63946',fontWeight:'600'}}>
            🤖 NC → POE
          </button>
          {esAdmin&&<button onClick={()=>setVista('nuevo')} style={{padding:'6px 14px',background:Q.azulVivo,color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>+ Nuevo POE</button>}
        </div>
      </div>

      <PanelARIA/>

      <div style={{padding:'1.2rem 1.5rem',maxWidth:'860px',margin:'0 auto'}}>
        {alertasVencidos.length>0&&(
          <div style={{background:oscuro?'#1a1000':'#fffbeb',border:'2px solid #f59e0b',borderRadius:'12px',padding:'12px 16px',marginBottom:'1rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <p style={{fontSize:'13px',fontWeight:'700',color:'#f59e0b',margin:0}}>⚠️ {alertasVencidos.length} POE{alertasVencidos.length>1?'s':''} con revisión vencida</p>
              <p style={{fontSize:'11px',color:sub,margin:0}}>{alertasVencidos.map(p=>p.codigo).join(', ')}</p>
            </div>
            <button onClick={ariaAlertaVencidos} style={{padding:'6px 12px',background:'#f59e0b',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontSize:'11px',fontWeight:'700'}}>🤖 ARIA analiza</button>
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:'10px',marginBottom:'1.5rem'}}>
          {[['Total',poes.length,Q.azulVivo],['Vigentes',poes.filter(p=>p.estado==='vigente').length,'#22c55e'],['Borradores',poes.filter(p=>p.estado==='borrador').length,'#f59e0b'],['Vencidos',alertasVencidos.length,'#e63946']].map(([l,v,c])=>(
            <div key={l} style={{background:cardBg,borderRadius:'14px',padding:'1rem',border:`1px solid ${borde}`,textAlign:'center'}}>
              <p style={{fontSize:'24px',fontWeight:'800',color:c,margin:'0 0 4px'}}>{v}</p>
              <p style={{fontSize:'11px',color:sub,margin:0,fontWeight:'600'}}>{l}</p>
            </div>
          ))}
        </div>

        <div style={{position:'relative',marginBottom:'10px'}}>
          <input placeholder="Buscar..." value={busqueda} onChange={e=>setBusqueda(e.target.value)} style={{...inp,marginTop:0,paddingLeft:'36px'}}/>
          <span style={{position:'absolute',left:'12px',top:'50%',transform:'translateY(-50%)',color:sub,fontSize:'14px'}}>🔍</span>
        </div>
        <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'1.2rem'}}>
          <button onClick={()=>setFiltroEstado('todos')} style={{padding:'4px 12px',borderRadius:'16px',fontSize:'11px',cursor:'pointer',fontWeight:'700',border:filtroEstado==='todos'?`2px solid ${Q.azulVivo}`:`1px solid ${borde}`,background:filtroEstado==='todos'?`${Q.azulVivo}15`:cardBg,color:filtroEstado==='todos'?Q.azulVivo:sub}}>Todos</button>
          {Object.entries(ESTADOS_POE).map(([k,v])=>(
            <button key={k} onClick={()=>setFiltroEstado(k)} style={{padding:'4px 12px',borderRadius:'16px',fontSize:'11px',cursor:'pointer',fontWeight:'700',border:filtroEstado===k?`2px solid ${v.color}`:`1px solid ${borde}`,background:filtroEstado===k?`${v.color}22`:cardBg,color:filtroEstado===k?v.color:sub}}>{v.label}</button>
          ))}
        </div>

        {poesFiltrados.length===0?(
          <div style={{textAlign:'center',padding:'5rem 2rem'}}>
            <p style={{fontSize:'48px'}}>📄</p>
            <p style={{fontSize:'15px',color:texto,fontWeight:'600',marginTop:'1rem'}}>Sin POEs</p>
            <p style={{fontSize:'13px',color:sub}}>{esAdmin?'Tocá "+ Nuevo POE" para comenzar.':'Sin POEs cargados.'}</p>
          </div>
        ):poesFiltrados.map(p=>{
          const estadoInfo=ESTADOS_POE[p.estado]||{};
          const vencido = alertasVencidos.some(v=>v.id===p.id);
          return (
            <div key={p.id} onClick={()=>{setPoeActual(p);setSeccionActiva('info');setVista('detalle');}}
              style={{background:cardBg,borderRadius:'14px',padding:'1.2rem 1.5rem',border:`1.5px solid ${vencido?'#f59e0b':borde}`,marginBottom:'10px',cursor:'pointer',transition:'box-shadow 0.15s'}}
              onMouseEnter={e=>e.currentTarget.style.boxShadow=`0 4px 20px ${Q.azulVivo}20`}
              onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',gap:'8px',marginBottom:'4px',flexWrap:'wrap'}}>
                    <span style={{fontWeight:'700',color:Q.azulVivo,fontSize:'13px'}}>{p.codigo}</span>
                    <span style={{fontSize:'13px',color:texto,fontWeight:'600'}}>{p.titulo}</span>
                    <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'6px',background:`${estadoInfo.color}22`,color:estadoInfo.color}}>{estadoInfo.label}</span>
                    {vencido&&<span style={{fontSize:'10px',fontWeight:'700',padding:'2px 8px',borderRadius:'6px',background:'#fef9c3',color:'#a16207'}}>⚠️ Rev. vencida</span>}
                  </div>
                  <p style={{fontSize:'12px',color:sub,margin:'0 0 6px'}}>{p.categoria} · v{p.version} · {p.frecuenciaRevision}</p>
                  <div style={{display:'flex',gap:'10px',fontSize:'11px',color:sub,flexWrap:'wrap'}}>
                    <span>🔢 {(p.pasos||[]).length} pasos</span>
                    <span>✍️ {(p.lecturas||[]).length} lecturas</span>
                    {(p.modulos||[]).slice(0,3).map(m=>{const mod=MODULOS_ALQOS.find(x=>x.id===m);return mod?<span key={m}>{mod.icon} {mod.label}</span>:null;})}
                  </div>
                </div>
                <span style={{fontSize:'11px',color:sub,flexShrink:0,marginLeft:'10px'}}>{p.fechaCreacion?.slice(0,10)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}